import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../components/formsComponents";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";

import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { LoadState, MODULE_IDS, ROLES } from "../../../../../../../constants/enums";
import { InsurerCodeAutoSearch } from "../../../../../../../components/AutoCompleteSearches";
import { ILifeInsurancePolicyBasicDetailsProps } from "../LifeInsuranceBasicDetails.types";
import {
  addLifeInsurancePolicyAdminManagerAsync,
  fetchLifeInsurancePolicyAdminManagerAsync,
} from "../../../../../../../redux/lifeInsurancePolicy/lifeInsuranceBasicDetail/lifeInsuranceBasicDetailActions";
import { ComissionCodeSelect } from "../../../../../../../components/AutoCompleteSearches/ComissionCodeSelect";
import { UsersAutoSearchByRole } from "../../../../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { fetchBranchOfficeWithCallback } from "../../../../../../../redux/branchOffice/branchOfficeActions";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { useRoles } from "../../../../../../../security/RolesProvider/RolesProvider";

export const LifeInsurancePolicyAdminManager: React.FC<
  ILifeInsurancePolicyBasicDetailsProps
> = (props) => {
  const { customerpolicyId } = props;
  const { data, Loading } = useSelector(
    (storeState: IStoreState) =>
      storeState.lifeInsurance.basicDetails.policyAdminManager
  );
  const dispatch = useDispatchWrapper();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const { roleName } = useRoles();
  const isProducerDisabled = [ROLES.CSR, ROLES.PRODUCER, ROLES.CSR_HEADOFFICE].includes(roleName as ROLES);

  const { personalInformation } = useSelector(
    (storeState: IStoreState) => storeState.customer
  );
  const {
    values,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    errors,
  } = useFormik({
    initialValues: data,
    validate: (values) => {
      const errors: any = {};
      return errors;
    },
    onSubmit: async (values) => {
      setSaveLoading(true);
      dispatch(
        addLifeInsurancePolicyAdminManagerAsync(
          {
            ...values,
            customer_policy_id: customerpolicyId,
          },
          (isSuccess) => {
            if (isSuccess) {
              setSaveLoading(false);
            }
          }
        )
      );
    },
  });

  React.useEffect(() => {
    dispatch(fetchLifeInsurancePolicyAdminManagerAsync(customerpolicyId));
  }, [customerpolicyId]);

  React.useEffect(() => {
    setValues({
      ...data,
      branch_code: personalInformation.data.branch_code,
      producer_one: data.producer_one || personalInformation.data.producer,
    });
  }, [data]);

  React.useEffect(() => {
    if (personalInformation.data.branch_code) {
      dispatch(
        fetchBranchOfficeWithCallback(
          personalInformation.data.branch_code,
          (data) => {
            setFieldValue("aga", data.name);
          }
        )
      );
    }
  }, [personalInformation]);

  return (
    <PageLoader loading={Loading === LoadState.InProgress}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Insurer</CustomFormLabel>
            <InsurerCodeAutoSearch
              value={values.insurer}
              onSelect={(value) => {
                setFieldValue("insurer", value.value);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Comission Code</CustomFormLabel>
            <ComissionCodeSelect
              insurerCode={values.insurer}
              value={values.commission_code}
              onChange={(value) => {
                setFieldValue("commission_code", value);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>AGA</CustomFormLabel>
            <CustomTextField
              name="aga"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              disabled
              value={values.aga}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>AGA Code</CustomFormLabel>
            <CustomTextField
              name="branch_code"
              fullWidth
              value={values.branch_code}
              disabled
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <UsersAutoSearchByRole
              label="Advisor 1"
              value={values.producer_one}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                setFieldValue("producer_one", data.user_fact_id);
                setFieldValue("producer_one_code", data.user_code);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <CustomFormLabel>%</CustomFormLabel>
            <CustomTextField
              fullWidth
              value={values.producer_one_new_percentage}
              name="producer_one_new_percentage"
              variant="outlined"
              size="small"
              type="text"
              disabled={isProducerDisabled}
              onChange={handleChange}
              // disabled
            />
          </Grid>
          <Grid item xs={12} lg={4}></Grid>

          <Grid item xs={12} lg={4}>
            <UsersAutoSearchByRole
              label="Advisor 2"
              value={values.producer_two}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                setFieldValue("producer_two", data.user_fact_id);
                setFieldValue("producer_two_code", data.user_code);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <CustomFormLabel>%</CustomFormLabel>
            <CustomTextField
              fullWidth
              value={values.producer_two_new_percentage}
              name="producer_two_new_percentage"
              variant="outlined"
              size="small"
              type="text"
              disabled={isProducerDisabled}
              onChange={handleChange}
              // disabled
            />
          </Grid>
          <Grid item xs={12} lg={4}></Grid>

          <Grid item xs={12} lg={4}>
            <UsersAutoSearchByRole
              label="Advisor 3"
              value={values.producer_three}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                setFieldValue("producer_three", data.user_fact_id);
                setFieldValue("producer_three_code", data.user_code);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <CustomFormLabel>%</CustomFormLabel>
            <CustomTextField
              fullWidth
              value={values.producer_three_new_percentage}
              name="producer_three_new_percentage"
              variant="outlined"
              size="small"
              type="text"
              disabled={isProducerDisabled}
              onChange={handleChange}
              // disabled
            />
          </Grid>
          <Grid item xs={12} lg={4}></Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              value={values.csr}
              label="CSR"
              onSelect={(value) => {
                setFieldValue("csr", value.user_fact_id);
                setFieldValue("csr_code", value.user_code);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Status</CustomFormLabel>
            <ControlledCustomSelect
              name="status"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              displayEmpty
              placeholder="Select One"
              options={[
                { label: "Active", value: "ACTIVE" },
                { label: "Inactive", value: "INACTIVE" },
              ].map((data) => {
                return { label: data.label, value: data.value };
              })}
              value={values.status}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <RoleBasedCustomButton
            type="submit"
            moduleKey={MODULE_IDS.LIFE_AND_FINCANCIAL}
            // disabled={saveLoading === SaveState.Progress}
            variant="contained"
          >
            Save
          </RoleBasedCustomButton>
        </Box>
      </form>
    </PageLoader>
  );
};
